import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "../../app/router";
import {
  formatBytes,
  unpackPackage,
  type TransferLimits,
  type UnpackedTransferFile,
} from "../../packaging";
import {
  isValidSessionId,
  shortSessionCode,
  TransferFrameAssembler,
  type FrameAcceptResult,
  type FrameAssemblyProgress,
} from "../../protocol";
import { CameraScanner } from "./cameraScanner";
import {
  ReceiverProgressStore,
  type ReceiverQrSessionStatus,
  type SaveReceiverQrSessionInput,
} from "./receiverProgressStore";
import { useTransferLimits } from "./useTransferLimits";

const EMPTY_LIMIT_OVERRIDES: Partial<TransferLimits> = {};

type ReceiverStatus =
  | "invitation-invalid"
  | "loading-resume"
  | "camera-intro"
  | "requesting-camera"
  | "camera-ready"
  | "paused"
  | "background-paused"
  | "reconstructing"
  | "verified"
  | "camera-error"
  | "frame-conflict"
  | "deleted";

export interface ReceiverTransferProps {
  sessionId?: string;
  limits?: Partial<TransferLimits>;
}

function describeRejectedFrame(
  result: Extract<FrameAcceptResult, { status: "rejected" }>,
): string {
  switch (result.reason) {
    case "wrong-session":
      return "That QR belongs to a different transfer. Keep this invitation open and scan the matching sender.";
    case "corrupt":
      return "A corrupted frame was rejected. Hold both devices steady and keep scanning.";
    case "conflicting-duplicate":
    case "conflicting-transfer":
      return "Conflicting valid frames were detected. Stop and restart from a new sender session.";
    case "unsupported-version":
      return "That frame uses an unsupported QR protocol version.";
    case "bounds":
      return "A frame exceeded this app's configured safety limits and was rejected.";
    case "invalid-session":
    case "malformed":
      return "An unreadable QR was ignored. Keep the complete sender code inside the guide.";
  }
}

function saveFile(file: UnpackedTransferFile): void {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  link.rel = "noopener";
  link.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1_000);
}

export function ReceiverTransfer({
  sessionId,
  limits: limitOverrides = EMPTY_LIMIT_OVERRIDES,
}: ReceiverTransferProps) {
  const limits = useTransferLimits(limitOverrides);
  const validSessionId =
    sessionId !== undefined && isValidSessionId(sessionId) ? sessionId : undefined;
  const assembler = useMemo(
    () =>
      validSessionId === undefined
        ? undefined
        : new TransferFrameAssembler(validSessionId, limits),
    [limits, validSessionId],
  );
  const progressStore = useMemo(() => new ReceiverProgressStore(), []);
  const scannerRef = useRef<CameraScanner | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement>(null);
  const processingQueueRef = useRef<Promise<void>>(Promise.resolve());
  const storageQueueRef = useRef<Promise<void>>(Promise.resolve());
  const processingGenerationRef = useRef(0);
  const deletedRef = useRef(false);
  const finalizingRef = useRef(false);
  const closeTimerRef = useRef<number | undefined>(undefined);
  const [status, setStatus] = useState<ReceiverStatus>(
    validSessionId === undefined ? "invitation-invalid" : "loading-resume",
  );
  const [progress, setProgress] = useState<FrameAssemblyProgress>(
    assembler?.progress ?? {
      metadataReceived: 0,
      dataReceived: 0,
      uniqueReceived: 0,
      duplicateCount: 0,
      rejectedCount: 0,
      hasConflict: false,
      readyToFinalize: false,
    },
  );
  const [verifiedFiles, setVerifiedFiles] = useState<UnpackedTransferFile[]>([]);
  const [feedback, setFeedback] = useState<string>();
  const [storageWarning, setStorageWarning] = useState<string>();
  const [shareMessage, setShareMessage] = useState<string>();

  const persistReceiverSession = useCallback(
    async (
      nextStatus: ReceiverQrSessionStatus,
      details: Omit<SaveReceiverQrSessionInput, "sessionId" | "status"> = {},
    ): Promise<void> => {
      if (validSessionId === undefined || deletedRef.current) {
        return;
      }
      const operation = storageQueueRef.current.then(async () => {
        if (deletedRef.current) {
          return;
        }
        await progressStore.saveReceiverSession({
          ...details,
          sessionId: validSessionId,
          status: nextStatus,
        });
      });
      storageQueueRef.current = operation.catch(() => undefined);
      try {
        await operation;
      } catch {
        if (!deletedRef.current) {
          setStorageWarning(
            "This receiving session could not be indexed for later recovery. Keep this invitation URL or page open.",
          );
        }
      }
    },
    [progressStore, validSessionId],
  );

  const finalizeTransfer = useCallback(async (): Promise<void> => {
    if (
      assembler === undefined ||
      deletedRef.current ||
      finalizingRef.current ||
      !assembler.progress.readyToFinalize
    ) {
      return;
    }

    finalizingRef.current = true;
    scannerRef.current?.stop();
    setStatus("reconstructing");
    setFeedback("All unique frames are present. Verifying every byte.");
    try {
      const assembled = await assembler.finalize();
      const unpacked = await unpackPackage(
        assembled.bytes,
        assembled.packageSha256,
        limits,
      );
      if (deletedRef.current) {
        return;
      }
      if (unpacked.files.length !== assembled.descriptor.fileCount) {
        throw new Error(
          "Verified package file count conflicts with transfer metadata.",
        );
      }
      setVerifiedFiles(unpacked.files);
      setStatus("verified");
      setFeedback(unpacked.files.length === 1 ? "File verified" : "Files verified");
      await persistReceiverSession("verified", {
        fileCount: assembled.descriptor.fileCount,
        preparedBytes: assembled.descriptor.packageByteLength,
        ...(assembler.progress.metadataTotal === undefined ||
        assembler.progress.dataTotal === undefined
          ? {}
          : {
              frameCount:
                assembler.progress.metadataTotal + assembler.progress.dataTotal,
            }),
        receivedFrames: assembler.progress.uniqueReceived,
      });
    } catch (error) {
      if (deletedRef.current) {
        return;
      }
      finalizingRef.current = false;
      setStatus("frame-conflict");
      await persistReceiverSession("conflict");
      setFeedback(
        error instanceof Error
          ? error.message
          : "Integrity verification failed. No files were exposed.",
      );
    }
  }, [assembler, limits, persistReceiverSession]);

  const acceptFrameText = useCallback(
    async (text: string, persist: boolean): Promise<void> => {
      if (assembler === undefined || deletedRef.current) {
        return;
      }

      const previousProgress = assembler.progress;
      const result = await assembler.accept(text);
      if (deletedRef.current) {
        return;
      }
      setProgress(result.progress);
      if (result.status === "accepted") {
        if (persist) {
          const operation = storageQueueRef.current.then(async () => {
            if (!deletedRef.current) {
              await progressStore.saveFrame(result.frame);
            }
          });
          storageQueueRef.current = operation.catch(() => undefined);
          try {
            await operation;
          } catch {
            if (!deletedRef.current) {
              setStorageWarning(
                "This valid frame could not be saved for reload recovery. Keep this page open and continue scanning.",
              );
            }
          }
        }
        if (deletedRef.current) {
          return;
        }
        const metadataCompleted =
          result.progress.metadataTotal !== undefined &&
          result.progress.metadataReceived === result.progress.metadataTotal &&
          (previousProgress.metadataTotal === undefined ||
            previousProgress.metadataReceived < previousProgress.metadataTotal);
        const completedSegment = result.progress.dataSegments?.find(
          (segment) =>
            segment.complete &&
            previousProgress.dataSegments?.[segment.index]?.complete !== true,
        );
        if (metadataCompleted) {
          setFeedback("Transfer metadata complete.");
        } else if (completedSegment !== undefined) {
          setFeedback(`Segment ${String(completedSegment.index + 1)} complete.`);
        }
        if (result.progress.readyToFinalize) {
          await finalizeTransfer();
        }
        return;
      }
      if (result.status === "duplicate") {
        return;
      }

      setFeedback(describeRejectedFrame(result));
      if (
        result.reason === "conflicting-duplicate" ||
        result.reason === "conflicting-transfer"
      ) {
        scannerRef.current?.stop();
        setStatus("frame-conflict");
        await persistReceiverSession("conflict");
      }
    },
    [assembler, finalizeTransfer, persistReceiverSession, progressStore],
  );

  useEffect(() => {
    if (assembler === undefined || validSessionId === undefined) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        await persistReceiverSession("paused");
        const savedFrames = await progressStore.loadFrameTexts(validSessionId);
        for (const text of savedFrames) {
          if (cancelled || deletedRef.current) {
            return;
          }
          await acceptFrameText(text, false);
        }
        if (!cancelled && !deletedRef.current && !assembler.progress.readyToFinalize) {
          setProgress(assembler.progress);
          setStatus("camera-intro");
          await persistReceiverSession("paused", {
            receivedFrames: assembler.progress.uniqueReceived,
            ...(assembler.progress.uniqueTotal === undefined
              ? {}
              : { frameCount: assembler.progress.uniqueTotal }),
          });
          if (savedFrames.length > 0) {
            setFeedback(
              `Restored ${assembler.progress.dataReceived.toLocaleString()} unique data frames from this device.`,
            );
          }
        }
      } catch {
        if (!cancelled && !deletedRef.current) {
          setStorageWarning(
            "Saved progress could not be loaded. You can still scan while this page remains open.",
          );
          setStatus("camera-intro");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    acceptFrameText,
    assembler,
    persistReceiverSession,
    progressStore,
    validSessionId,
  ]);

  useEffect(() => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
    return () => {
      scannerRef.current?.stop();
      closeTimerRef.current = window.setTimeout(() => {
        void processingQueueRef.current
          .catch(() => undefined)
          .then(() => storageQueueRef.current.catch(() => undefined))
          .then(async () => progressStore.close());
      }, 0);
    };
  }, [progressStore]);

  useEffect(() => {
    function handleVisibilityChange(): void {
      if (document.visibilityState === "hidden" && status === "camera-ready") {
        scannerRef.current?.stop();
        setStatus("background-paused");
        setFeedback(
          "Scanning paused while this page was in the background. Prepare the camera again to continue.",
        );
        void persistReceiverSession("paused", {
          receivedFrames: progress.uniqueReceived,
          ...(progress.uniqueTotal === undefined
            ? {}
            : { frameCount: progress.uniqueTotal }),
        });
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [persistReceiverSession, progress.uniqueReceived, progress.uniqueTotal, status]);

  async function prepareCamera(): Promise<void> {
    const video = videoRef.current;
    if (video === null || assembler === undefined || deletedRef.current) {
      return;
    }

    setStatus("requesting-camera");
    setFeedback(undefined);
    const scanner = new CameraScanner({
      onCode(text) {
        const generation = processingGenerationRef.current;
        processingQueueRef.current = processingQueueRef.current
          .then(async () => {
            if (deletedRef.current || generation !== processingGenerationRef.current) {
              return;
            }
            await acceptFrameText(text, true);
          })
          .catch(() => {
            if (!deletedRef.current) {
              setFeedback(
                "A camera frame could not be processed. Keep scanning; accepted progress is preserved.",
              );
            }
          });
      },
      onError() {
        setFeedback(
          "The current camera image could not be read. Hold both devices steady, reduce glare, or slow the sender.",
        );
      },
    });
    scannerRef.current?.stop();
    scannerRef.current = scanner;

    try {
      await scanner.start(video);
      if (deletedRef.current) {
        scanner.stop();
        return;
      }
      setStatus("camera-ready");
      await persistReceiverSession("receiving", {
        receivedFrames: progress.uniqueReceived,
        ...(progress.uniqueTotal === undefined
          ? {}
          : { frameCount: progress.uniqueTotal }),
      });
      setFeedback(
        "Camera ready. Tell the sender to press Start sending and keep the full QR inside the guide.",
      );
    } catch (error) {
      scanner.stop();
      if (deletedRef.current) {
        return;
      }
      setStatus("camera-error");
      setFeedback(
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "Camera permission was denied. Allow camera access in browser settings, then try again."
          : "The camera could not start. Check camera access, close other camera apps, and try again.",
      );
    }
  }

  function pauseCamera(): void {
    if (deletedRef.current) {
      return;
    }
    scannerRef.current?.stop();
    setStatus("paused");
    void persistReceiverSession("paused", {
      receivedFrames: progress.uniqueReceived,
      ...(progress.uniqueTotal === undefined
        ? {}
        : { frameCount: progress.uniqueTotal }),
    });
    setFeedback("Scanning paused. Accepted progress remains on this device.");
  }

  async function shareVerifiedFiles(): Promise<void> {
    if (verifiedFiles.length === 0 || navigator.share === undefined) {
      setShareMessage("System sharing is unavailable here. Use Save file instead.");
      return;
    }

    const files = verifiedFiles.map(
      (file) =>
        new File([file.blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        }),
    );
    const shareData: ShareData = {
      files,
      title:
        verifiedFiles.length === 1
          ? (files[0]?.name ?? "Verified file")
          : "Verified files",
    };
    if (navigator.canShare !== undefined && !navigator.canShare(shareData)) {
      setShareMessage("This browser cannot share these files. Use Save file instead.");
      return;
    }

    try {
      await navigator.share(shareData);
      setShareMessage("The system share sheet accepted the verified files.");
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setShareMessage("Sharing failed. The verified files remain available to save.");
      }
    }
  }

  async function deleteLocalSession(): Promise<void> {
    if (validSessionId === undefined) {
      return;
    }
    const previousStatus = status;
    deletedRef.current = true;
    processingGenerationRef.current += 1;
    scannerRef.current?.stop();
    scannerRef.current = undefined;
    try {
      await processingQueueRef.current.catch(() => undefined);
      await storageQueueRef.current.catch(() => undefined);
      await progressStore.clearSession(validSessionId);
      finalizingRef.current = false;
      setVerifiedFiles([]);
      setProgress({
        metadataReceived: 0,
        dataReceived: 0,
        uniqueReceived: 0,
        duplicateCount: 0,
        rejectedCount: 0,
        hasConflict: false,
        readyToFinalize: false,
      });
      setStorageWarning(undefined);
      setShareMessage(undefined);
      setStatus("deleted");
      setFeedback(
        "Local receiving progress and reconstructed files were removed from this page.",
      );
    } catch {
      deletedRef.current = false;
      setStatus(previousStatus === "verified" ? "verified" : "paused");
      setFeedback(
        "Local progress could not be deleted. Check browser storage access and try again.",
      );
    }
  }

  if (validSessionId === undefined || assembler === undefined) {
    return (
      <section aria-labelledby="qr-receiver-title" className="qr-transfer__panel">
        <p className="qr-transfer__eyebrow">Local-only QR</p>
        <h2 id="qr-receiver-title">Invitation invalid</h2>
        <p className="qr-transfer__message qr-transfer__message--danger">
          This invitation is missing a valid v1 session. Ask the sender to show a new
          invitation QR.
        </p>
      </section>
    );
  }

  const dataTotal = progress.dataTotal;
  const progressPercent =
    dataTotal === undefined || dataTotal === 0
      ? 0
      : Math.min(100, (progress.dataReceived / dataTotal) * 100);
  const completedSegmentCount =
    progress.dataSegments?.filter((segment) => segment.complete).length ?? 0;
  const hasMultipleSegments = (progress.dataSegments?.length ?? 0) > 1;
  const suggestedSegment =
    progress.suggestedSegmentIndex === undefined
      ? undefined
      : progress.dataSegments?.[progress.suggestedSegmentIndex];
  const metadataTotal = progress.metadataTotal;
  const metadataMissing =
    metadataTotal === undefined
      ? undefined
      : Math.max(0, metadataTotal - progress.metadataReceived);
  const metadataComplete = metadataMissing === 0;
  const allDataComplete =
    dataTotal !== undefined && progress.dataReceived === dataTotal;
  const cameraVisible = status === "requesting-camera" || status === "camera-ready";

  return (
    <section
      aria-labelledby="qr-receiver-title"
      className="qr-transfer__panel qr-transfer__receiver"
    >
      <header className="qr-transfer__header">
        <div>
          <p className="qr-transfer__eyebrow">Local-only QR</p>
          <h2 id="qr-receiver-title">Receive files by QR</h2>
        </div>
        <span className="qr-transfer__status">
          {status === "camera-ready"
            ? "Camera ready"
            : status === "reconstructing"
              ? "Verifying"
              : status === "verified"
                ? verifiedFiles.length === 1
                  ? "File verified"
                  : "Files verified"
                : status === "loading-resume"
                  ? "Restoring progress"
                  : status === "paused" || status === "background-paused"
                    ? "Paused"
                    : status === "deleted"
                      ? "Deleted"
                      : "Ready to receive"}
        </span>
      </header>

      <p className="qr-transfer__privacy">
        Scanning and reconstruction happen on this device. The visible QR stream is not
        encrypted, and a first visit may need internet to load the app.
      </p>
      <div className="qr-transfer__recovery-link">
        <span>Accepted frames stay available in Activity if you pause or leave.</span>
        <button
          className="qr-transfer__button qr-transfer__button--quiet"
          onClick={() => navigate("/activity")}
          type="button"
        >
          Open saved QR sessions
        </button>
      </div>

      {status === "deleted" ? (
        <div className="qr-transfer__verified">
          <h3>Local session removed</h3>
          <p>
            Saved frames and reconstructed files for this receiving session were deleted
            from this device.
          </p>
          <button
            className="qr-transfer__button qr-transfer__button--secondary"
            onClick={() => navigate("/activity")}
            type="button"
          >
            View saved QR sessions
          </button>
        </div>
      ) : status === "verified" ? (
        <div className="qr-transfer__verified">
          <div aria-hidden="true" className="qr-transfer__verified-mark">
            ✓
          </div>
          <h3>{verifiedFiles.length === 1 ? "File verified" : "Files verified"}</h3>
          <p>
            Every chunk, the complete package, and each extracted file passed SHA-256
            verification.
          </p>
          <ul className="qr-transfer__file-list">
            {verifiedFiles.map((file) => (
              <li key={`${file.name}-${file.sha256}`}>
                <div>
                  <strong>{file.name}</strong>
                  <small>
                    {file.type} · {formatBytes(file.size)}
                  </small>
                </div>
                <button
                  className="qr-transfer__button qr-transfer__button--secondary"
                  onClick={() => {
                    saveFile(file);
                  }}
                  type="button"
                >
                  Save file
                </button>
              </li>
            ))}
          </ul>
          <div className="qr-transfer__actions">
            <button
              className="qr-transfer__button qr-transfer__button--primary"
              onClick={() => {
                void shareVerifiedFiles();
              }}
              type="button"
            >
              Share
            </button>
            <button
              className="qr-transfer__button qr-transfer__button--danger"
              onClick={() => {
                void deleteLocalSession();
              }}
              type="button"
            >
              Delete local transfer
            </button>
          </div>
          {shareMessage === undefined ? null : (
            <p className="qr-transfer__hint">{shareMessage}</p>
          )}
        </div>
      ) : (
        <>
          <div className="qr-transfer__receiver-grid">
            <div
              className={`qr-transfer__camera ${
                cameraVisible ? "qr-transfer__camera--active" : ""
              }`}
            >
              <video aria-label="QR scanning camera" ref={videoRef} playsInline />
              <div aria-hidden="true" className="qr-transfer__scan-guide" />
              {!cameraVisible ? (
                <div className="qr-transfer__camera-placeholder">
                  <strong>Camera is off</strong>
                  <span>Camera images stay in this browser and are not stored.</span>
                </div>
              ) : null}
            </div>

            <div className="qr-transfer__stage-copy">
              <h3>Session {shortSessionCode(validSessionId)}</h3>
              <div className="qr-transfer__progress-copy">
                <strong>
                  {progress.dataReceived.toLocaleString()} /{" "}
                  {dataTotal?.toLocaleString() ?? "?"} unique data frames
                </strong>
                <span>
                  {dataTotal === undefined
                    ? "Waiting for transfer metadata"
                    : `${Math.max(0, dataTotal - progress.dataReceived).toLocaleString()} missing`}
                </span>
              </div>
              <progress
                aria-label="Unique transfer frames received"
                max={100}
                value={progressPercent}
              />
              <p className="qr-transfer__hint">
                Duplicate scans: {progress.duplicateCount.toLocaleString()} · Rejected:{" "}
                {progress.rejectedCount.toLocaleString()}
              </p>

              <div className="qr-transfer__progress-copy">
                <strong>
                  {progress.metadataReceived.toLocaleString()} /{" "}
                  {metadataTotal?.toLocaleString() ?? "?"} metadata frames
                </strong>
                <span>
                  {metadataTotal === undefined
                    ? "Waiting for frame totals"
                    : metadataComplete
                      ? "Metadata complete"
                      : `${metadataMissing?.toLocaleString() ?? "?"} metadata missing`}
                </span>
              </div>
              <progress
                aria-label="Transfer metadata frames received"
                max={metadataTotal ?? 1}
                value={progress.metadataReceived}
              />
              {allDataComplete && !metadataComplete ? (
                <p className="qr-transfer__message qr-transfer__message--warning">
                  All data segments are complete, but transfer metadata is still
                  missing. Ask the sender to keep any loop target running; every target
                  repeats metadata.
                </p>
              ) : null}

              {progress.dataSegments === undefined ? null : (
                <section
                  aria-labelledby="qr-segment-progress-title"
                  className="qr-transfer__segments"
                >
                  <div className="qr-transfer__segments-header">
                    <div>
                      <h4 id="qr-segment-progress-title">Frame segments</h4>
                      <p>
                        {completedSegmentCount.toLocaleString()} /{" "}
                        {progress.dataSegments.length.toLocaleString()} complete
                      </p>
                    </div>
                    {suggestedSegment === undefined ? null : (
                      <strong>
                        {hasMultipleSegments
                          ? `Ask sender for Segment ${String(suggestedSegment.index + 1)}`
                          : "Keep Entire transfer looping"}
                      </strong>
                    )}
                  </div>
                  <ol className="qr-transfer__segment-list">
                    {progress.dataSegments.map((segment) => (
                      <li
                        className={
                          segment.complete
                            ? "is-complete"
                            : segment.received > 0
                              ? "is-partial"
                              : "is-pending"
                        }
                        key={segment.index}
                      >
                        <div className="qr-transfer__segment-copy">
                          <strong>Segment {segment.index + 1}</strong>
                          <span>
                            {segment.complete
                              ? "Complete"
                              : `${segment.missing.toLocaleString()} missing`}
                          </span>
                        </div>
                        <progress
                          aria-label={`Segment ${String(segment.index + 1)} data frames received`}
                          max={segment.total}
                          value={segment.received}
                        />
                        <small>
                          {segment.received.toLocaleString()} /{" "}
                          {segment.total.toLocaleString()} · data frames{" "}
                          {(segment.startDataIndex + 1).toLocaleString()}–
                          {segment.endDataIndexExclusive.toLocaleString()}
                        </small>
                      </li>
                    ))}
                  </ol>
                  <p className="qr-transfer__hint">
                    {hasMultipleSegments
                      ? "These segment numbers match the sender’s Loop target selector. Completed data segments do not need their data frames repeated."
                      : "This transfer has one data segment. The sender’s Entire transfer loop carries it and repeats the metadata."}
                  </p>
                </section>
              )}

              {storageWarning === undefined ? null : (
                <p className="qr-transfer__message qr-transfer__message--warning">
                  {storageWarning}
                </p>
              )}
              {feedback === undefined ? null : (
                <p aria-live="polite" className="qr-transfer__message">
                  {feedback}
                </p>
              )}

              <div className="qr-transfer__actions">
                {status === "camera-ready" ? (
                  <button
                    className="qr-transfer__button qr-transfer__button--secondary"
                    onClick={pauseCamera}
                    type="button"
                  >
                    Pause
                  </button>
                ) : status !== "reconstructing" && status !== "frame-conflict" ? (
                  <button
                    className="qr-transfer__button qr-transfer__button--primary"
                    disabled={status === "requesting-camera"}
                    onClick={() => {
                      void prepareCamera();
                    }}
                    type="button"
                  >
                    {status === "requesting-camera"
                      ? "Requesting camera"
                      : "Prepare camera"}
                  </button>
                ) : null}
                {progress.uniqueReceived > 0 ? (
                  <button
                    className="qr-transfer__button qr-transfer__button--danger"
                    onClick={() => {
                      void deleteLocalSession();
                    }}
                    type="button"
                  >
                    Delete local data
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {status === "frame-conflict" ? (
            <p className="qr-transfer__message qr-transfer__message--danger">
              Verification stopped. No partial file is available. Ask the sender to
              create a new session.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
